/**
 * @file interpret/engine/interpretController.ts
 * @brief One live interpretation session: mic → server → translation → mic.
 *
 * @details
 * The web port of the session logic inside the phone's `SileroConversationScreen`,
 * pulled out of the screen so it can be tested and so the screen only renders.
 * Everything it touches is injected (`InterpretDeps`); `browserDeps.ts` wires the
 * real mic, socket, player and API.
 *
 * The loop, as on the phone (walkie-talkie half-duplex, free-flow speakers):
 * 1. Listening: mic frames stream to the server; its Silero VAD ends turns.
 *    Either person may speak, any number of turns in a row.
 * 2. A `final` arrives: the uplink closes, the turn joins the transcript and its
 *    translation plays through the speakers.
 * 3. Playback really ends → "your turn" beep → uplink reopens → Listening.
 * Turns that arrive while one is playing wait in a FIFO queue.
 *
 * Billing, exactly as the phone (`docs/product/web-interpretation-plan.md`):
 * - Order of start: mic first (free), then `/s2s/start` (card hold), then the
 *   socket. If the socket never connects the hold is released with `/s2s/cancel`.
 * - Billable time = elapsed minus every pause, INCLUDING a pause still open when
 *   End is pressed (the phone's 06-07-2026 hole). Snapshotted before teardown.
 * - Pausing is refused while a translation is playing (`canTogglePause`).
 * - Switching tabs does NOT pause (DES, 01-10-2026); a lost mic does.
 *
 * Exposes `subscribe` / `getSnapshot` for `useSyncExternalStore`.
 *
 * @author DES
 * @date 01-10-2026
 */

// `.ts`: the controller also runs under node:test, which needs the extension.
import { CallFloor, type FloorPlayback } from "./callFloor.ts";
import type { TurnAudioSource } from "./endpoints";
import type { PcmCapture, PcmFrameHandler } from "./micCapture";
import type { StartSessionInput, StartedSession } from "./s2sApi";
import type { S2sConnection, S2sFinal, S2sSocketEvents } from "./s2sSocket";
import type { KeepAwake } from "./wakeLock";

export type MicStream = {
  stop(): void;
  /** Called once if the device goes away (unplugged, permission revoked). */
  onEnded(cb: () => void): void;
  /** Call mode: RMS of your mic and of the call's audio, for `CallFloor`. */
  levels?(): { mic: number; call: number };
  /** Call mode: which sources reach the uplink. */
  setSources?(s: { mic: boolean; call: boolean }): void;
};

export type SocketPort = {
  connect(sessionId: string): Promise<void>;
  sendAudio(base64: string, samples: number): void;
  endTurn(): void;
  pause(): void;
  resume(): void;
  stop(): void;
};

export type PlayerPort = {
  /** `callSinkId`: Call mode's output device (the Verblizr virtual mic),
   *  readied in the same click so it is never autoplay-blocked.
   *  `hearSinkId`: where you hear translations (your headphones); null =
   *  the Mac's sound output. */
  unlock(
    callSinkId?: string | null,
    hearSinkId?: string | null,
    opts?: { boostHere?: number },
  ): void;
  /** `toCall`: play into the call device instead of the headphones. */
  play(
    source: TurnAudioSource,
    authToken: string | null,
    opts?: { toCall?: boolean; alsoHere?: boolean },
  ): Promise<unknown>;
  /** Where you hear translations, changed during the session (optional). */
  setHearSink?(hearSinkId: string | null): void;
  /** Seconds into the playing translation and its length, for the caption.
   *  Optional: a player without it shows the caption by its own clock. */
  progress?(): { currentSec: number; durationSec: number } | null;
  playBeep(opts?: { toCall?: boolean }): Promise<void>;
  stop(): void;
  close(): void;
};

export type InterpretDeps = {
  api: {
    start(input: StartSessionInput): Promise<StartedSession>;
    end(conversationId: string, totalSeconds: number): Promise<void>;
    /** `billed: true` when someone had spoken: billed for the time used (BACKEND 00145). */
    cancel(conversationId: string): Promise<{ billed: boolean } | void>;
    /** `/end` that survives the page unloading (fetch keepalive). */
    endOnUnload(conversationId: string, totalSeconds: number): void;
  };
  /** `callAudio`: Call mode, the mic mixed with the call's audio. */
  openMic(deviceId?: string, opts?: { callAudio?: boolean }): Promise<MicStream>;
  /** Call mode, "your voice, then the translation": your live mic into the
   *  call device. Absent where there is no such device (browser). */
  openCallSend?(mic: MicStream, deviceId: string): Promise<CallSend>;
  startCapture(stream: MicStream, onFrame: PcmFrameHandler): Promise<PcmCapture>;
  createSocket(opts: { fromLang: string; toLang: string; events: S2sSocketEvents }): SocketPort;
  player: PlayerPort;
  resolveAudio(final: S2sFinal, conversationId: string): TurnAudioSource | null;
  authToken(): string | null;
  keepAwake(): KeepAwake;
  now(): number;
  /**
   * (02-10-2026) The "your turn" beep after each translation and on Resume.
   * Default OFF on DES's call (the phone's `FEATURE_FLAGS.turnBeep` matches):
   * it was barely audible and the mic could pick it up as a short empty turn.
   * Pass true to bring it back.
   */
  turnBeep?: boolean;
  /** Repeating timer (Call mode's floor); tests drive it by hand. */
  every?(ms: number, fn: () => void): () => void;
};

export type InterpretPhase =
  | "idle"
  | "starting"
  | "listening"
  | "translating"
  | "playing"
  | "paused"
  | "ending"
  | "ended"
  | "error";

export type InterpretTurn = {
  id: string;
  speaker: "A" | "B";
  srcLang: string;
  tgtLang: string;
  srcText: string;
  tgtText: string;
  at: number;
  /** Server round trip for this turn (the phone's RTT pill). */
  latencyMs?: number;
};

export type InterpretError = { code: string; message: string };

export type InterpretSnapshot = {
  phase: InterpretPhase;
  conversationId: string | null;
  currencyCode: string | null;
  ratePerMin: number | null;
  turns: InterpretTurn[];
  /** The translation playing right now, for the caption. */
  playing: InterpretTurn | null;
  connection: S2sConnection;
  noiseWarning: boolean;
  /** Fatal problem (session could not start / had to end). */
  error: InterpretError | null;
  /** Passing problem worth a toast (one turn failed, reconnecting). */
  notice: string | null;
  /** The transcript turn being replayed (paused only), for its Stop glyph. */
  replayingId: string | null;
};

/**
 * Call mode (desktop app, docs/product/desktop-call-interpretation-plan.md):
 * Person A = you (the mic), Person B = the other side (the call's audio).
 * - Their translation plays in your headphones.
 * - `sendDeviceId`: the device the call app uses as its mic (the Verblizr
 *   virtual mic). Your translation plays into it; null = keep it here.
 * - `translationOnly` false (the default): your voice also goes into the
 *   call live, and the translation follows when your turn ends.
 * - `hearDeviceId`: where you hear their translation (your headphones, even
 *   when the Mac's own output is still its speakers); null = the Mac's output.
 */
export type CallRoute = {
  sendDeviceId: string | null;
  translationOnly: boolean;
  hearDeviceId?: string | null;
  /** Your translation in your headphones too, as it goes into the call. */
  hearOwn?: boolean;
  /** A soft beep into the call after your translation: "your turn". */
  turnBeep?: boolean;
  /** Their translation into the call too, so they can check it (it still
   *  plays here). */
  theyHearOwn?: boolean;
  /** Translations sound on the Mac's speakers (no headphones): `CallFloor`
   *  shuts your mic while they do. */
  speakers?: boolean;
};

/** Your live voice into the call device; on only while the uplink is open. */
export type CallSend = { setLive(on: boolean): void; stop(): void };

export type StartParams = {
  langA: string;
  langB: string;
  deviceId?: string;
  session: Omit<StartSessionInput, "srcLang" | "tgtLang">;
  call?: CallRoute;
};

const INITIAL: InterpretSnapshot = {
  phase: "idle",
  conversationId: null,
  currencyCode: null,
  ratePerMin: null,
  turns: [],
  playing: null,
  connection: "idle",
  noiseWarning: false,
  error: null,
  notice: null,
  replayingId: null,
};

/**
 * Auto-pause after this long with nobody speaking (docs/product/desktop-minutes-cap.md):
 * paused time is not billed, so a session left running on a laptop stops
 * costing the user. Resume is one tap, as after any pause.
 */
export const IDLE_PAUSE_MS = 10 * 60_000;
/** Frame RMS that counts as someone speaking, for the idle timer only. */
const ACTIVITY_RMS = 0.035;

const LIVE: ReadonlySet<InterpretPhase> = new Set([
  "listening",
  "translating",
  "playing",
  "paused",
]);

type Queued = { turn: InterpretTurn; audio: TurnAudioSource | null };

function errorOf(e: unknown, fallbackCode: string, fallbackMessage: string): InterpretError {
  const o = (e ?? {}) as { code?: unknown; message?: unknown; name?: unknown };
  const code =
    typeof o.code === "string" && o.code
      ? o.code
      : typeof o.name === "string" && o.name && o.name !== "Error"
        ? o.name
        : fallbackCode;
  const message = typeof o.message === "string" && o.message ? o.message : fallbackMessage;
  return { code, message };
}

/** Call mode: translations here at +18 dB into a compressor and a ceiling,
 *  against macOS ducking during a call (+8 dB, then +12 dB, were still too
 *  quiet on the Mac's speakers in DES's WhatsApp tests). */
const CALL_HERE_BOOST = 8;

/** How often Call mode re-reads the levels (callFloor.ts). */
const FLOOR_TICK_MS = 50;

/** A cancel after speech is billed for the time used (BACKEND 00145, 17-10-2026). */
export const BILLED_NOTICE = "Billed for the time used.";

export class InterpretController {
  private readonly deps: InterpretDeps;
  private snap: InterpretSnapshot = INITIAL;
  private readonly listeners = new Set<() => void>();

  private langA = "";
  private langB = "";
  private mic: MicStream | null = null;
  private call: CallRoute | null = null;
  private callSend: CallSend | null = null;
  /** Call mode with a mic that reports levels: the floor replaces the
   *  half-duplex gate (callFloor.ts). */
  private floor: CallFloor | null = null;
  private stopFloor: (() => void) | null = null;
  private playback: FloorPlayback = "none";
  /** The playing translation also sounds here. */
  private playbackHere = false;
  /** Call mode: your live voice stays out of the call (switchable live). */
  private translationOnly = false;
  private capture: PcmCapture | null = null;
  private socket: SocketPort | null = null;
  private awake: KeepAwake | null = null;
  private uplinkOpen = false;
  private queue: Queued[] = [];
  /**
   * Each turn's translation audio, kept for replay from the transcript (the
   * phone's play button). Live turns have no saved original, as on the phone.
   * Inline mp3s of ~50-150 KB; dropped with the session.
   */
  private turnAudio = new Map<string, TurnAudioSource>();
  private busy = false;
  private stopping = false;
  private turnSeq = 0;

  private startedAt: number | null = null;
  private endedAt: number | null = null;
  private pausedMs = 0;
  private pauseStartedAt: number | null = null;
  private lastActivityAt = 0;

  constructor(deps: InterpretDeps) {
    this.deps = deps;
  }

  // ---- store ---------------------------------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): InterpretSnapshot => this.snap;

  private set(patch: Partial<InterpretSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const l of this.listeners) l();
  }

  /** A method, not a field read: the phase changes across awaits. */
  private phase(): InterpretPhase {
    return this.snap.phase;
  }

  /** Pause-excluded session time so far (the number `/s2s/end` is billed on). */
  getBillableMs(): number {
    if (this.startedAt === null) return 0;
    const end = this.endedAt ?? this.deps.now();
    const openPause = this.pauseStartedAt !== null ? end - this.pauseStartedAt : 0;
    return Math.max(0, end - this.startedAt - this.pausedMs - openPause);
  }

  /** Mic level 0..1 (RMS of the latest frame), for the glow. Read per frame, not stored. */
  getLevel(): number {
    return this.capture?.level() ?? 0;
  }

  get isLive(): boolean {
    return LIVE.has(this.phase());
  }

  // ---- lifecycle -------------------------------------------------------------

  /** Call from the Start click: the player must be unlocked inside the gesture. */
  async start(params: StartParams): Promise<void> {
    const { phase } = this.snap;
    if (phase !== "idle" && phase !== "ended" && phase !== "error") return;
    this.call = params.call ?? null;
    // Call mode: translations here are played louder, as the call app turns
    // other apps down while it runs (macOS ducking).
    this.deps.player.unlock(this.call?.sendDeviceId ?? null, this.call?.hearDeviceId ?? null, {
      boostHere: this.call ? CALL_HERE_BOOST : 1,
    });

    this.langA = params.langA;
    this.langB = params.langB;
    this.resetSessionState();
    this.set({ ...INITIAL, phase: "starting" });

    // 1. Mic first: a refused permission costs nothing.
    try {
      this.mic = await this.deps.openMic(
        params.deviceId,
        this.call ? { callAudio: true } : undefined,
      );
    } catch (e) {
      this.fail(errorOf(e, "MIC_UNAVAILABLE", "We couldn't use your microphone."));
      return;
    }
    if (this.stopping) {
      this.teardown();
      return;
    }
    this.mic.onEnded(() => this.onMicLost());

    // Call mode: your live voice into the call (option 2). Opened even with
    // "Only send the translation" on, which can be switched during the
    // session (DES 03-10-2026); it just stays silent then. A missing device
    // costs only that: the session still interprets, as a notice says.
    this.translationOnly = !!this.call?.translationOnly;
    const sendTo = this.call?.sendDeviceId;
    if (sendTo && this.deps.openCallSend) {
      try {
        this.callSend = await this.deps.openCallSend(this.mic, sendTo);
      } catch {
        this.set({
          notice: "We couldn't send your voice to the call. Translations still play here.",
        });
      }
    }

    // 2. Session + card hold.
    let started: StartedSession;
    try {
      started = await this.deps.api.start({
        ...params.session,
        srcLang: params.langA,
        tgtLang: params.langB,
      });
    } catch (e) {
      this.teardown();
      this.fail(errorOf(e, "S2S_START_FAILED", "We couldn't start the session. Please try again."));
      return;
    }
    if (this.stopping) {
      // Ended while the hold was being placed: release it, nothing was used.
      const billed = await this.cancelHold(started.conversationId);
      this.set({ phase: "ended", ...(billed ? { notice: BILLED_NOTICE } : {}) });
      return;
    }
    this.set({
      conversationId: started.conversationId,
      currencyCode: started.currencyCode,
      ratePerMin: started.ratePerMin,
    });

    // 3. Socket. If it never connects, release the hold: nothing was used.
    const socket = this.deps.createSocket({
      fromLang: params.langA,
      toLang: params.langB,
      events: this.socketEvents(),
    });
    this.socket = socket;
    try {
      await socket.connect(started.conversationId);
      this.capture = await this.deps.startCapture(this.mic, (f) => this.onFrame(f));
    } catch (e) {
      const userEnded = this.stopping;
      this.teardown();
      const billed = await this.cancelHold(started.conversationId);
      if (userEnded) {
        this.set({ phase: "ended", ...(billed ? { notice: BILLED_NOTICE } : {}) });
        return;
      }
      const err = errorOf(e, "WS_CONNECT_FAILED", "We couldn't reach the interpreter. You have not been charged.");
      this.fail(
        billed
          ? { ...err, message: `${err.message.replace(/\s*You have not been charged\.?/, "")} ${BILLED_NOTICE}`.trim() }
          : err,
      );
      return;
    }
    if (this.stopping) {
      // Ended while connecting: release the hold, nothing was used.
      this.teardown();
      const billed = await this.cancelHold(started.conversationId);
      this.set({ phase: "ended", ...(billed ? { notice: BILLED_NOTICE } : {}) });
      return;
    }

    this.startedAt = this.deps.now();
    this.lastActivityAt = this.startedAt;
    this.awake = this.deps.keepAwake();
    this.openUplink();
    this.set({ phase: "listening" });
    if (this.call && this.mic.levels && this.mic.setSources) {
      this.floor = new CallFloor();
      const every =
        this.deps.every ??
        ((ms: number, fn: () => void) => {
          const id = setInterval(fn, ms);
          return () => clearInterval(id);
        });
      this.stopFloor = every(FLOOR_TICK_MS, () => this.floorTick());
      this.floorTick();
    }
  }

  /** Call mode: apply who holds the floor (callFloor.ts). */
  floorTick(): void {
    if (!this.floor || !this.mic?.levels) return;
    // Paused: nothing from here. The desktop app's helper passes your real
    // mic into Verblizr Microphone while no interpretation runs (driver
    // 1.3.0), so they hear you directly; sending it here too would double it.
    if (this.phase() === "paused") {
      this.callSend?.setLive(false);
      return;
    }
    const levels = this.mic.levels();
    const out = this.floor.step({
      now: this.deps.now(),
      ...levels,
      playback: this.playback,
      hearOwn: this.playbackHere,
      speakers: !!this.call?.speakers,
    });
    this.mic.setSources?.({ mic: out.micToUplink, call: out.callToUplink });
    // We know who is speaking from the two sources, so we end the turn
    // rather than wait for the server's VAD, which hears both.
    if (out.endTurn && this.uplinkOpen) this.socket?.endTurn();
    const live = this.uplinkOpen && out.micLive && !this.translationOnly;
    this.callSend?.setLive(live);
    // Development only: what the floor decided, readable from the console
    // (`__vzFloor`) while tuning on real calls.
    if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      (globalThis as { __vzFloor?: unknown }).__vzFloor = {
        at: Date.now(),
        mic: Math.round(levels.mic * 1000) / 1000,
        call: Math.round(levels.call * 1000) / 1000,
        floor: out.floor,
        micToUplink: out.micToUplink,
        live,
        hasCallSend: !!this.callSend,
        speakers: !!this.call?.speakers,
        playback: this.playback,
      };
    }
  }

  /**
   * Headphones plugged in or out during the session (DES 03-10-2026: after
   * plugging EarPods in mid-session the other side was no longer heard, as
   * the speakers mode, set at Start, still sent only the mic). Switches where
   * translations play and the speakers / headphones mode at once.
   */
  setCallOutput(o: { hearDeviceId: string | null; speakers: boolean }): void {
    if (!this.call) return;
    if (this.call.hearDeviceId === o.hearDeviceId && !!this.call.speakers === o.speakers) return;
    this.call = { ...this.call, hearDeviceId: o.hearDeviceId, speakers: o.speakers };
    this.deps.player.setHearSink?.(o.hearDeviceId);
    if (this.floor) this.floorTick();
  }

  /** Call mode sends to a call device (the switch below applies). */
  sendsToCall(): boolean {
    return !!this.call?.sendDeviceId;
  }

  /** "Only send the translation", switched during the session. */
  setTranslationOnly(on: boolean): void {
    this.translationOnly = on;
    if (this.call) this.call = { ...this.call, translationOnly: on };
    if (this.floor) this.floorTick();
    else this.callSend?.setLive(this.uplinkOpen && !on);
  }

  /** Finish the current turn now: the noisy-room backup (mic tap / Space). */
  endTurn(): void {
    if (this.phase() === "listening") this.socket?.endTurn();
  }

  /** The phone's `canTogglePause`: not mid-translation, only while live. */
  canPause(): boolean {
    return this.phase() === "listening" || this.phase() === "translating";
  }

  pause(): void {
    if (!this.canPause()) return;
    this.pauseStartedAt = this.deps.now();
    this.closeUplink();
    this.socket?.pause();
    this.set({ phase: "paused" });
    if (this.floor) this.floorTick();
  }

  async resume(): Promise<void> {
    if (this.phase() !== "paused" || this.pauseStartedAt === null) return;
    this.stopReplay();
    this.pausedMs += this.deps.now() - this.pauseStartedAt;
    this.pauseStartedAt = null;
    this.socket?.resume();
    if (this.queue.length > 0) {
      // A translation that arrived during the pause plays first.
      this.set({ phase: "listening" });
      void this.drainQueue();
      return;
    }
    this.set({ phase: "listening" });
    if (this.deps.turnBeep) await this.deps.player.playBeep().catch(() => undefined);
    if (this.phase() === "listening" && !this.busy) this.openUplink();
  }

  /** Whether a turn has audio to replay (its play button shows). */
  /** The playing translation's position, polled by the caption. */
  playbackProgress(): { currentSec: number; durationSec: number } | null {
    return this.deps.player.progress?.() ?? null;
  }

  hasTurnAudio(turnId: string): boolean {
    return this.turnAudio.has(turnId);
  }

  /**
   * The transcript's play button (the phone's): replay a turn's translation,
   * only while paused so it never mixes with live audio or reaches the mic.
   * Pressing the playing turn again stops it.
   */
  async replayTurn(turnId: string): Promise<"played" | "stopped" | "not-paused" | "no-audio"> {
    if (this.phase() !== "paused") return "not-paused";
    if (this.snap.replayingId === turnId) {
      this.stopReplay();
      return "stopped";
    }
    const src = this.turnAudio.get(turnId);
    if (!src) return "no-audio";
    this.deps.player.stop();
    this.set({ replayingId: turnId });
    try {
      await this.deps.player.play(src, this.deps.authToken());
    } catch {
      this.set({ notice: "No audio available for this turn." });
    }
    if (this.snap.replayingId === turnId) this.set({ replayingId: null });
    return "played";
  }

  stopReplay(): void {
    if (this.snap.replayingId === null) return;
    this.set({ replayingId: null });
    this.deps.player.stop();
  }

  /** End and bill. Safe to call twice. */
  async end(): Promise<void> {
    if (!this.isLive && this.phase() !== "starting") return;
    const conversationId = this.snap.conversationId;
    // Snapshot the bill BEFORE teardown, an open pause included.
    this.endedAt = this.deps.now();
    const billableSec = this.getBillableMs() / 1000;
    this.set({ phase: "ending", playing: null, replayingId: null });
    this.teardown();
    if (!conversationId || this.startedAt === null) {
      // Still starting: `start()` sees `stopping` and releases any hold.
      this.set({ phase: "ended" });
      return;
    }
    try {
      await this.deps.api.end(conversationId, billableSec);
      this.set({ phase: "ended" });
    } catch (e) {
      // The server sweeper finalizes an unended session; tell the user plainly.
      this.set({
        phase: "ended",
        notice: errorOf(
          e,
          "S2S_END_FAILED",
          "We couldn't close the session. It will be closed for you shortly.",
        ).message,
      });
    }
  }

  /**
   * The tab is closing (`pagehide`): bill the time used with a keepalive
   * request, synchronously, then release the mic. No awaiting is possible here.
   */
  endOnUnload(): void {
    if (!this.isLive) return;
    const conversationId = this.snap.conversationId;
    this.endedAt = this.deps.now();
    const billableSec = this.getBillableMs() / 1000;
    this.teardown();
    this.set({ phase: "ended" });
    if (conversationId && this.startedAt !== null) {
      this.deps.api.endOnUnload(conversationId, billableSec);
    }
  }

  /** Release everything without calling the API (unmount after `end`). */
  dispose(): void {
    this.teardown();
    this.deps.player.close();
    // Listeners are left to their own unsubscribe: under React StrictMode the
    // same controller is unmounted and mounted again in development.
  }

  clearNotice(): void {
    if (this.snap.notice) this.set({ notice: null });
  }

  // ---- internals -------------------------------------------------------------

  private resetSessionState(): void {
    this.queue = [];
    this.turnAudio.clear();
    this.busy = false;
    this.stopping = false;
    this.uplinkOpen = false;
    this.startedAt = null;
    this.endedAt = null;
    this.pausedMs = 0;
    this.pauseStartedAt = null;
  }

  private openUplink(): void {
    if (this.stopping) return;
    this.uplinkOpen = true;
    this.capture?.setGate(true);
    // The silence clock starts again here. A closed gate delivers no frames
    // (micCapture), so the pause / translation before this never reached
    // onFrame's "not listening counts as activity" branch: a resume after a
    // long pause was re-paused by its first quiet frame (09-10-2026, DES).
    this.lastActivityAt = this.deps.now();
    // Your voice reaches the call while you hold the floor, never over a
    // translation. With a floor, its tick decides.
    if (this.floor) this.floorTick();
    else this.callSend?.setLive(!this.translationOnly);
  }

  private closeUplink(): void {
    this.uplinkOpen = false;
    this.capture?.setGate(false);
    this.callSend?.setLive(false);
  }

  /** `/s2s/cancel`; true when the server billed the time used instead. */
  private async cancelHold(conversationId: string): Promise<boolean> {
    try {
      const res = await this.deps.api.cancel(conversationId);
      return !!(res && res.billed);
    } catch {
      return false;
    }
  }

  private teardown(): void {
    this.stopping = true;
    this.closeUplink();
    this.queue = [];
    this.capture?.stop();
    this.capture = null;
    this.callSend?.stop();
    this.callSend = null;
    this.stopFloor?.();
    this.stopFloor = null;
    this.floor = null;
    this.playback = "none";
    this.mic?.stop();
    this.mic = null;
    this.socket?.stop();
    this.socket = null;
    this.deps.player.stop();
    this.awake?.release();
    this.awake = null;
  }

  private fail(error: InterpretError): void {
    this.set({ phase: "error", error, playing: null });
  }

  private onFrame(f: { base64: string; samples: number; rms: number }): void {
    if (this.uplinkOpen) this.socket?.sendAudio(f.base64, f.samples);
    const phase = this.phase();
    if (phase !== "listening") {
      // Translating / playing / paused all count as activity.
      this.lastActivityAt = this.deps.now();
      return;
    }
    const now = this.deps.now();
    if (f.rms > ACTIVITY_RMS) this.lastActivityAt = now;
    else if (now - this.lastActivityAt > IDLE_PAUSE_MS) {
      this.pause();
      this.set({
        notice:
          "Paused after 10 minutes of silence, so you are not charged. Tap the mic to resume.",
      });
    }
  }

  private onMicLost(): void {
    if (!this.isLive) return;
    if (this.canPause()) this.pause();
    this.set({ notice: "Your microphone was disconnected. Reconnect it, then tap to resume." });
  }

  private socketEvents(): S2sSocketEvents {
    return {
      onStatus: (s) => {
        const { phase } = this.snap;
        if (s === "translating" && phase === "listening") this.set({ phase: "translating" });
        else if (s === "listening" && phase === "translating" && !this.busy)
          this.set({ phase: "listening" });
      },
      onFinal: (f) => this.onFinal(f),
      onNoiseWarning: (show) => this.set({ noiseWarning: show }),
      onConnection: (c) => this.set({ connection: c }),
      onError: (err) => {
        if (err.recoverable) {
          this.set({ notice: err.message });
          return;
        }
        if (this.isLive) {
          this.set({ error: { code: err.code ?? "WS_FATAL", message: err.message } });
          void this.end();
        }
      },
    };
  }

  private onFinal(f: S2sFinal): void {
    if (this.stopping || !this.snap.conversationId) return;
    // A turn has ended: Call mode's turn-length cap starts again.
    this.floor?.noteTurnEnded();
    if (!f.asr.trim() && !f.mt.trim()) return;
    const speaker: "A" | "B" = f.speaker === "B" ? "B" : "A";
    this.turnSeq += 1;
    const turn: InterpretTurn = {
      id: `t${this.turnSeq}`,
      speaker,
      srcLang: speaker === "A" ? this.langA : this.langB,
      tgtLang: speaker === "A" ? this.langB : this.langA,
      srcText: f.asr,
      tgtText: f.mt,
      at: this.deps.now(),
      latencyMs: f.e2eServerMs,
    };
    const audio = this.deps.resolveAudio(f, this.snap.conversationId);
    if (audio) this.turnAudio.set(turn.id, audio);
    this.set({ turns: [...this.snap.turns, turn] });
    this.queue.push({ turn, audio });
    void this.drainQueue();
  }

  private async drainQueue(): Promise<void> {
    if (this.busy || this.stopping || this.phase() === "paused") return;
    if (this.queue.length === 0) return;
    this.busy = true;
    try {
      let played = false;
      for (;;) {
        if (this.stopping || this.phase() === "paused") return;
        const item = this.queue.shift();
        if (!item) break;
        // Call mode keeps listening during a translation: the floor shuts
        // only your mic, and only when it would hear the playback.
        if (!this.floor) this.closeUplink();
        if (!item.audio) continue;
        this.set({ phase: "playing", playing: item.turn });
        try {
          // Call mode: your translation goes into the call, theirs to you.
          // Each side can also hear its own: you here ("Hear my translation
          // too"), they in the call ("They hear their translation too").
          const mine = item.turn.speaker === "A";
          const toCall = !!this.call?.sendDeviceId && (mine || !!this.call?.theyHearOwn);
          const alsoHere = toCall && (mine ? !!this.call?.hearOwn : true);
          this.playback = toCall ? "toCall" : "here";
          this.playbackHere = !toCall || alsoHere;
          if (this.floor) this.floorTick();
          try {
            await this.deps.player.play(item.audio, this.deps.authToken(), {
              toCall,
              alsoHere,
            });
            // Call mode: after YOUR translation, a soft beep into the call
            // only, so they know it is their turn (DES 04-10-2026). Your mic
            // never hears it, so it cannot make an empty turn.
            if (mine && toCall && this.call?.turnBeep) {
              await this.deps.player.playBeep({ toCall: true }).catch(() => undefined);
            }
          } finally {
            this.playback = "none";
          }
          played = true;
        } catch {
          this.set({ notice: "We couldn't play that translation. It is in the transcript." });
        }
        this.set({ playing: null });
      }
      if (this.stopping) return;
      this.set({ phase: "listening" });
      if (played && this.deps.turnBeep) await this.deps.player.playBeep().catch(() => undefined);
      if (!this.stopping && this.phase() === "listening" && this.queue.length === 0) {
        this.openUplink();
      }
    } finally {
      this.busy = false;
    }
    // A turn that landed during the beep.
    if (this.queue.length > 0) void this.drainQueue();
  }
}
